import sessionReader from '../../store/readHelpers/SessionReader';
import idVerificationActions, {
    changeStep,
    errorMessageTypes,
    similarityMessageTypes,
    stepTypes,
} from '../../actions/IdVerificationActions';

import { IdVerificationStatus } from '../../services/idverification/IdVerificationConstants';

import stateCache from '../StateCache';
import preCheckService from '../PreCheckService';
import storeManager from '../../store/StoreManager';
import navigationService from '../NavigationService';

/**
 * needsIdCard indicates whether the ID verification should show the ID step or
 * if it has already been completed before.
 *
 * The must be stable in a session: if it returns true, it must
 * continue to return true until the end of the session, even if the local
 * storage has already been modified. To make this possible I use the
 * isAfterPhotoStep argument.
 *
 * If we skipped the face photo step, this necessarily means that this function
 * returned true, since otherwise the Test Taker would not have been able to go
 * beyond the face photo step. So in this case, we keep returning true
 * regardless of the local storage.
 *
 * This stability ensures that the step is marked as completed once the ID photo
 * is captured. Otherwise, the isIdVerificationComplete forces the Test
 * Taker to take a photo of the face again because the completion of the
 * identity verification depends on the need to capture the ID.
 */

const idVerificationService = {
    needsIdVerification() {
        return sessionReader.isAutomaticIdVerificationEnabled();
    },

    needsIdCard(isAfterPhotoStep) {
        if (isAfterPhotoStep) {
            return true;
        }

        const { idverification } = storeManager.getState();

        return !stateCache.getStateCacheInstance().isInitialIdVerificationCompleted()
            && !this.isRequestPhotoRetakeVerificationStatus(idverification);
    },

    isSimilarityCheckPending(state) {
        return state.currentStep === stepTypes.SIMILARITY
            && state.verificationStatus === similarityMessageTypes.PENDING_AUTOMATIC;
    },

    isSimilarityStep({ currentStep }) {
        return currentStep === stepTypes.SIMILARITY;
    },

    isIdCardStep({ currentStep }) {
        return currentStep === stepTypes.ID_CARD;
    },

    isSimilarityCheckFailed(state) {
        const { verificationStatus } = state;

        return (
            state.currentStep === stepTypes.SIMILARITY
            && (
                verificationStatus === similarityMessageTypes.REJECTED
                || verificationStatus === errorMessageTypes.requestWentWrong
            )
        );
    },

    isSimilarityCheckApproved(state) {
        return state.currentStep === stepTypes.SIMILARITY
            && state.verificationStatus === similarityMessageTypes.APPROVED;
    },

    isIdVerificationComplete(state) {
        if (!this.needsIdVerification()) {
            return preCheckService.hardwareCheckCompleted();
        }

        if (!this.needsIdCard(state.currentStep !== stepTypes.PHOTO)) {
            return state.verificationStatus === errorMessageTypes.none;
        }

        return this.isSimilarityCheckApproved(state);
    },

    isCurrent() {
        return preCheckService.audioVideoCheckCompleted() && !preCheckService.idVerificationCompleted();
    },

    isRequestPhotoRetakeVerificationStatus(idVerification) {
        return idVerification.idVerificationStatus === IdVerificationStatus.REQUIRES_PHOTO_RETAKE;
    },

    gotoPhotoRetake() {
        storeManager.dispatch(changeStep(stepTypes.PHOTO));
        storeManager.dispatch(idVerificationActions.setManualIdVerificationRequestSent(false));
        navigationService.navigateToIdVerification();
    },
};

export default idVerificationService;
